// enables type checking for this file
// @ts-check

const browserData = Intl.DateTimeFormat().resolvedOptions();
const browserLocale = browserData.locale;

// Epoch/UTC TimeStamp info
// https://www.unixtimestamp.com/

/**
 * Converts UTC Seconds to a Date
 * @param {number} utcSeconds - Time in Seconds since Epoch
 * @returns {Date} - Date
 */
export function convertUTCsecondsToDate(utcSeconds) {
  // Set Epoch Date
  const date = new Date(0);
  date.setUTCSeconds(utcSeconds);
  return date;
}

/**
 * Converts UTC Milliseconds to a Date
 * @param {number} utcMs - Time in Milliseconds since Epoch
 */
export function convertUTCmillisecondsToDate(utcMs) {
  // Set Epoch Date
  const date = new Date(0);
  date.setUTCMilliseconds(utcMs);
  return date;
}

/**
 * Converts a Date to Browser Locale
 * @param {Date} date - Date to Convert
 * @returns {string} - Date in  browser locale
 */
export function dateToLocale(date) {
  return date.toLocaleString(browserLocale);
}

/**
 * Converts a UTC Seconds to Browser Locale Date
 * @param {number} utcSeconds - UTC Seconds to Convert
 */
export function utcSecondsToLocaleDate(utcSeconds) {
  return dateToLocale(convertUTCsecondsToDate(utcSeconds));
}

/**
 * Converts a UTC Milliseconds to Browser Locale Date
 * @param {number} utcMs - UTC Seconds to Convert
 *  @returns {string} - Date in  browser locale
 */
export function utcMillisecondsToLocaleDate(utcMs) {
  return dateToLocale(convertUTCmillisecondsToDate(utcMs));
}

/**
 * Converts a UTC ms to Browser Locale Relative Time (Duration)
 * @param {number} utcSeconds - UTC ms to Convert
 *  @returns {string} - relative time (duration) in browser locale
 */
export function utcSecondsToRelative(utcSeconds) {
  return getRelativeTimeString(convertUTCsecondsToDate(utcSeconds));
}

/**
 * Converts a UTC Seconds to Browser Locale Relative Time (Duration)
 * @param {number} UTCseconds - UTC Seconds to Convert
 * @returns {string} - relative time (duration) in browser locale
 */
export function utcMillisecondsToRelative(UTCseconds) {
  return getRelativeTimeString(convertUTCmillisecondsToDate(UTCseconds));
}

/**
 * Convert a date to a relative time string, such as
 * "a minute ago", "in 2 hours", "yesterday", "3 months ago", etc.
 * using Intl.RelativeTimeFormat
 *
 *  From: https://www.builder.io/blog/relative-time
 * and converted to JS by KB 29/01/2024
 * @param {Date} date - Date
 * @param {string} locale - Browser Locale
 * @returns {string} - relative time (duration) in browser locale
 */
export function getRelativeTimeString(date, locale = browserLocale) {
  // Get the amount of seconds between the given date and now
  const deltaSeconds = Math.round((date.getTime() - Date.now()) / 1000);

  // Array representing one minute, hour, day, week, month, etc in seconds
  const cutoffs = [
    60,
    3600,
    86400,
    86400 * 7,
    86400 * 30,
    86400 * 365,
    Infinity,
  ];

  // Array equivalent to the above but in the string representation of the units
  /** @type {Intl.RelativeTimeFormatUnit[]} */
  const units = ['second', 'minute', 'hour', 'day', 'week', 'month', 'year'];

  // Grab the ideal cutoff unit
  const unitIndex = cutoffs.findIndex(
    (cutoff) => cutoff > Math.abs(deltaSeconds)
  );

  // Get the divisor to divide from the seconds. E.g. if our unit is "day" our divisor
  // is one day in seconds, so we can divide our seconds by this to get the # of days
  const divisor = unitIndex ? cutoffs[unitIndex - 1] : 1;

  // Intl.RelativeTimeFormat do its magic
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
  return rtf.format(Math.floor(deltaSeconds / divisor), units[unitIndex]);
}
